import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * useDetailsNavigation — where the Personal Details (/details) screen sends the
 * user AFTER their details are saved. Today that's straight to the business
 * category step (/business-step).
 *
 * Kept in a hook so the screen itself stays free of routing branches, and the
 * "what comes next" decision lives in ONE place (easy to change later without
 * touching the form). Wizard mode (PostPurchaseSetup) drives its own steps, so
 * we hand control back to it instead of navigating.
 *
 * Usage:
 *   const { goToNextStep } = useDetailsNavigation();
 *   goToNextStep({ wizardMode, onWizardNext });
 */
export default function useDetailsNavigation() {
  const navigate = useNavigate();

  const goToNextStep = useCallback(
    ({ wizardMode = false, onWizardNext, replace = false } = {}) => {
      // Wizard shell owns its own step sequence — let it advance.
      if (wizardMode && typeof onWizardNext === "function") {
        onWizardNext();
        return;
      }

      // Standalone flow: onboarding is far enough to mark the personal-details
      // stage complete, then go straight to the business category step. `replace`
      // is used from the sign-in redirect so Back doesn't return to login.
      localStorage.setItem("onboardComplete", "true");
      navigate("/business-step", { replace });
    },
    [navigate]
  );

  return { goToNextStep };
}
